import React, { useState } from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import Slideshow from "../Slideshow";
import Slide from "./Slide";
import { parseDate, formatRange } from "../../../helpers/dates";
import { isFuture } from "date-fns";
import { exhibitionShape } from "../shapes";

function formatDateRange(startString, endString) {
  const start = parseDate(startString);
  const end = parseDate(endString);

  return formatRange(start, end);
}

const Indicator = ({ active }) => (
  <svg
    viewBox="0 0 10 10"
    xmlns="http://www.w3.org/2000/svg"
    width="7"
    height="7"
    style={{ opacity: active ? 1 : 0.5 }}
  >
    <circle cx="5" cy="5" r="5" />
  </svg>
);

Indicator.displayName = "ExhibitionsOnView.Indicator";
Indicator.propTypes = {
  active: PropTypes.bool,
};

const TitleBlock = ({ exhibition }) => {
  const start = exhibition.start || exhibition.location_start;
  const end = exhibition.end || exhibition.location_end;
  const label = isFuture(parseDate(start)) ? "Upcoming" : "On View";
  const displayName = exhibition.display_name
    ? exhibition.display_name
    : exhibition.name;
  const sanitizedData = () => ({
    __html: DOMPurify.sanitize(displayName, { FORBID_TAGS: ["p"] }),
  });

  return (
    <>
      <p className="t-copy-label">{label}</p>
      <h2
        className="m-exhibition-header__title m-exhibition-header__title--desktop"
        dangerouslySetInnerHTML={sanitizedData()}
      />
      <a
        href={exhibition.url}
        className="m-exhibition-header__title m-exhibition-header__title--mobile"
      >
        <h2 dangerouslySetInnerHTML={sanitizedData()} />
      </a>
      <p className="m-exhibition-header__dates">
        {formatDateRange(start, end)}
      </p>
      <p className="m-exhibition-header__learn-more-link">
        <a href={exhibition.url} className="m-link-underlined">
          Learn More
        </a>
      </p>
    </>
  );
};

TitleBlock.displayName = "ExhibitionsOnView.TitleBlock";
TitleBlock.propTypes = {
  exhibition: PropTypes.shape(exhibitionShape).isRequired,
};

function ExhibitionsOnView({ exhibitions }) {
  const [active, setActive] = useState(0);

  const renderSlide = (exhibition) => (
    <Slide
      key={exhibition.id}
      imgDefault={exhibition.featured_image_default}
      imgRetina={exhibition.featured_image_retina}
      width={786}
      alt={exhibition.image_alt}
    />
  );

  return (
    <>
      <div className="m-exhibition-header__inner">
        <div className="m-exhibition-header__wide-block">
          <Slideshow
            renderSlide={renderSlide}
            slides={exhibitions}
            active={active}
            onUpdate={setActive}
          />
        </div>

        <div className="m-exhibition-header__narrow-block">
          <TitleBlock exhibition={exhibitions[active]} />
          <p className="m-exhibition-header__slideshow-dots">
            {exhibitions.map((exhibition, index) => (
              <Indicator key={index} active={index === active} />
            ))}
          </p>
        </div>
      </div>
    </>
  );
}

ExhibitionsOnView.propTypes = {
  exhibitions: PropTypes.arrayOf(PropTypes.shape(exhibitionShape).isRequired),
};

export default ExhibitionsOnView;
